import { Input } from "@/components/ui/input";
import { Link, MousePointerClick, Plus, Shuffle, X, AlertCircle } from "lucide-react";
import type { ButtonInput, UrlEntry } from "@/services/api/utility-templates";
import { useState, useEffect } from "react";

export function LocalInput({ value, onChange, type = "text", ...props }: any) {
    const [local, setLocal] = useState(value ?? "");

    useEffect(() => {
        setLocal(value ?? "");
    }, [value]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (local !== (value ?? "")) onChange(local);
        }, 400);
        return () => clearTimeout(t);
    }, [local, value, onChange]);

    return (
        <Input
            {...props}
            type={type}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => {
                if (local !== (value ?? "")) onChange(local);
            }}
        />
    );
}

interface ButtonsEditorProps {
    buttons: ButtonInput[];
    onChange: (buttons: ButtonInput[]) => void;
}

const EMPTY_URL_ENTRY: UrlEntry = { url: "", weight: 100 };

const getTotalWeight = (entries: UrlEntry[]) =>
    entries.reduce((sum, e) => sum + (Number(e.weight) || 0), 0);

export default function ButtonsEditor({ buttons, onChange }: ButtonsEditorProps) {
    const add = (type: "URL" | "POSTBACK") => {
        if (buttons.length >= 3) return;
        if (type === "URL") {
            onChange([...buttons, { type: "URL", text: "", url_base: "", url_suffix_example: "", url_entries: [{ ...EMPTY_URL_ENTRY }] }]);
        } else {
            onChange([...buttons, { type: "POSTBACK", text: "", payload: "" }]);
        }
    };

    const remove = (index: number) => {
        onChange(buttons.filter((_, i) => i !== index));
    };

    const update = (index: number, patch: Partial<ButtonInput>) => {
        onChange(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));
    };

    const addUrlEntry = (btnIndex: number) => {
        const entries = buttons[btnIndex].url_entries ?? [];
        const nextCount = entries.length + 1;
        const equalWeight = Math.floor(100 / nextCount);
        const newEntries: UrlEntry[] = entries.map((e) => ({ ...e, weight: equalWeight }));
        newEntries.push({ url: "", weight: 100 - equalWeight * (nextCount - 1) });
        update(btnIndex, { url_entries: newEntries });
    };

    const removeUrlEntry = (btnIndex: number, entryIndex: number) => {
        const entries = (buttons[btnIndex].url_entries ?? []).filter((_, i) => i !== entryIndex);
        update(btnIndex, { url_entries: entries });
    };

    const updateUrlEntry = (btnIndex: number, entryIndex: number, patch: Partial<UrlEntry>) => {
        const entries = (buttons[btnIndex].url_entries ?? []).map((e, i) =>
            i === entryIndex ? { ...e, ...patch } : e
        );
        update(btnIndex, { url_entries: entries });
    };

    return (
        <div className="px-4 pb-4 space-y-3">
            {/* Botões existentes */}
            {buttons.map((btn, i) => {
                const isUrl = btn.type === "URL";
                const entries = btn.url_entries || [];
                const totalWeight = getTotalWeight(entries);
                const weightOk = Math.round(totalWeight) === 100;

                return (
                    <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                        {/* Button header */}
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 border-b border-gray-100">
                            {isUrl ? (
                                <Link className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            ) : (
                                <MousePointerClick className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                            <span className="text-xs font-semibold text-gray-600 flex-1">
                                Botão {i + 1} — {isUrl ? "URL" : "Ação"}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-50"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        <div className="p-3 space-y-3">
                            {/* Texto do botão */}
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Texto do botão</label>
                                <LocalInput
                                    placeholder="Ex: 🍷 READ TEXT"
                                    value={btn.text}
                                    onChange={(v: string) => update(i, { text: v })}
                                    className="h-8 text-sm"
                                    maxLength={20}
                                />
                            </div>

                            {isUrl ? (
                                /* ── URL entries ── */
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                            <Shuffle className="w-3.5 h-3.5 text-blue-500" />
                                            <span className="text-xs font-semibold text-gray-600">URLs com peso (random)</span>
                                        </div>
                                        {entries.length > 0 && (
                                            <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded-full ${weightOk ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                                                {totalWeight}%
                                            </span>
                                        )}
                                    </div>

                                    {entries.map((entry, ei) => (
                                        <div key={ei} className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 space-y-1.5">
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-xs font-semibold text-blue-700">URL {ei + 1}</span>
                                                {entries.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeUrlEntry(i, ei)}
                                                        className="p-0.5 text-blue-400 hover:text-red-500"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                            <LocalInput
                                                value={entry.url}
                                                onChange={(v: string) => updateUrlEntry(i, ei, { url: v })}
                                                placeholder="{{DOMAIN}}{{URL1}}?utm_source={{UTM_SOURCE}}"
                                                className="h-7 text-xs bg-white"
                                            />
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-blue-600 whitespace-nowrap">Peso (%)</span>
                                                <LocalInput
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    placeholder="0"
                                                    value={entry.weight === 0 && entry.url === "" ? "" : entry.weight}
                                                    onChange={(v: string) => updateUrlEntry(i, ei, { weight: Number(v) || 0 })}
                                                    className="h-7 text-xs w-20 bg-white"
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {/* Weight warning */}
                                    {entries.length > 0 && !weightOk && (
                                        <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                            <span>A soma dos pesos deve ser 100%. Atual: <strong>{totalWeight}%</strong></span>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => addUrlEntry(i)}
                                        className="w-full py-1.5 text-xs border border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-blue-500 hover:text-blue-700 flex items-center justify-center gap-1"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        Adicionar URL
                                    </button>
                                </div>
                            ) : (
                                /* ── Postback ── */
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Payload</label>
                                    <LocalInput
                                        placeholder="payload"
                                        value={btn.payload ?? ""}
                                        onChange={(v: string) => update(i, { payload: v })}
                                        className="h-8 text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* Add buttons row */}
            {buttons.length < 3 && (
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => add("URL")}
                        className="flex-1 py-2 text-sm border border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex items-center justify-center gap-1.5"
                    >
                        <Link className="w-3.5 h-3.5" />
                        Botão URL
                    </button>
                    <button
                        type="button"
                        onClick={() => add("POSTBACK")}
                        className="flex-1 py-2 text-sm border border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 text-gray-500 hover:text-green-600 flex items-center justify-center gap-1.5"
                    >
                        <MousePointerClick className="w-3.5 h-3.5" />
                        Botão Ação
                    </button>
                </div>
            )}
        </div>
    );
}
